import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ComicPanel, ComicButton, ComicHeading } from '../components/ComicPanel';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify?token=${token}`);
      setStatus('success');
      setMessage(response.data.message);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed');
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-900">
        <ComicPanel className="w-full max-w-md p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-6 animate-spin" />
              <ComicHeading as="h1" className="text-2xl mb-4">
                Verifying...
              </ComicHeading>
              <p className="text-slate-400">Please wait while we verify your email.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <ComicHeading as="h1" className="text-2xl mb-4 text-green-500" data-testid="verify-success">
                Email Verified!
              </ComicHeading>
              <p className="text-slate-300 mb-8">{message}</p>
              <ComicButton variant="secondary" onClick={() => navigate('/dashboard')} data-testid="verify-go-dashboard">
                Go to Dashboard
              </ComicButton>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <ComicHeading as="h1" className="text-2xl mb-4 text-red-500" data-testid="verify-error">
                Verification Failed
              </ComicHeading>
              <p className="text-slate-300 mb-8">{message}</p>
              <Link to="/login">
                <ComicButton variant="primary">
                  Back to Login
                </ComicButton>
              </Link>
            </>
          )}
        </ComicPanel>
      </div>
    </Layout>
  );
};

const VerifyRequired = () => {
  const { user, resendVerification } = useAuth();
  const [sending, setSending] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerification();
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-900">
        <ComicPanel className="w-full max-w-md p-8 text-center">
          <Mail className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
          <ComicHeading as="h1" className="text-2xl mb-4" data-testid="verify-required-title">
            Verify Your Email
          </ComicHeading>
          <p className="text-slate-300 mb-4">
            We sent a verification email to:
          </p>
          <p className="text-yellow-500 font-bold mb-6">{user?.email}</p>
          <p className="text-slate-400 text-sm mb-8">
            Check your inbox (and spam folder) for the verification link.
          </p>
          <ComicButton 
            variant="ghost" 
            onClick={handleResend} 
            disabled={sending}
            data-testid="resend-verification"
          >
            {sending ? 'Sending...' : 'Resend Verification Email'}
          </ComicButton>
        </ComicPanel>
      </div>
    </Layout>
  );
};

export { VerifyEmail, VerifyRequired };
